import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ReactElement } from 'react';
import { Clock, Dumbbell, Trophy } from 'lucide-react';
import { useAuth } from './lib/auth';
import { ProShell } from './components/ProShell';
import Login from './pages/Login';
import Clienti from './pages/Clienti';
import Atleti from './pages/Atleti';
import Dashboard from './pages/Dashboard';
import Piani from './pages/Piani';
import PianoEditor from './pages/PianoEditor';
import Templates from './pages/Templates';
import Catalogo from './pages/Catalogo';
import ComingSoon from './pages/ComingSoon';
import Onboarding from './pages/Onboarding';
import Workout from './pages/Workout';
import WorkoutProgramEditor from './pages/WorkoutProgramEditor';
import Analytics from './pages/Analytics';
import Calendario from './pages/Calendario';
import Messaggi from './pages/Messaggi';
import Impostazioni from './pages/Impostazioni';
import Debug from './pages/Debug';

function ProtectedShell({ children }: { children: ReactElement }) {
  const { state, refresh } = useAuth();

  if (state.status === 'loading') {
    return (
      <div style={{ padding: 40, fontFamily: 'var(--sans)', color: 'var(--mute)' }}>
        Caricamento sessione…
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div style={{ padding: 40, fontFamily: 'var(--sans)', maxWidth: 540 }}>
        <h2 style={{ color: 'var(--red)', margin: 0 }}>Impossibile verificare la sessione</h2>
        <p style={{ color: 'var(--mute)', marginTop: 12, lineHeight: 1.55 }}>
          Errore: <code>{state.message}</code>
        </p>
        <p style={{ color: 'var(--mute)', marginTop: 6, lineHeight: 1.55 }}>
          Possibili cause: backend non raggiungibile, CORS/origin non in allowlist, oppure cookie cross-subdomain bloccato dal browser. Apri
          {' '}<a href="/debug" style={{ color: 'var(--green)' }}>/debug</a>{' '}per dettagli live.
        </p>
        <div style={{ marginTop: 20, display: 'flex', gap: 8 }}>
          <button
            type="button"
            onClick={refresh}
            style={{ padding: '10px 16px', background: 'var(--green)', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}
          >
            Riprova
          </button>
          <a
            href="/debug"
            style={{ padding: '10px 16px', background: 'var(--ink)', color: '#fff', borderRadius: 8, fontWeight: 700, textDecoration: 'none', fontFamily: 'var(--sans)' }}
          >
            Apri /debug
          </a>
        </div>
      </div>
    );
  }

  if (state.status === 'unauthenticated') {
    return <Navigate to="/login" replace />;
  }

  // status === 'authenticated'
  if (state.user.role !== 'dietologist') {
    return (
      <div style={{ padding: 40, fontFamily: 'var(--sans)', maxWidth: 540 }}>
        <h2>Accesso solo per professionisti</h2>
        <p style={{ color: 'var(--mute)', marginTop: 12, lineHeight: 1.55 }}>
          Sei loggato come <strong>{state.user.role}</strong> ({state.user.email}). Questo portale è dedicato a dietologi e nutrizionisti.
        </p>
        <p style={{ marginTop: 16 }}>
          <a href="/debug" style={{ color: 'var(--green)' }}>/debug</a> · <a href={`https://dietamenu.schedy.app/`} style={{ color: 'var(--mute)' }}>Torna alla SPA</a>
        </p>
      </div>
    );
  }

  return <ProShell>{children}</ProShell>;
}

/**
 * Sprint 6.A — routing landing role-aware.
 * Default route '/' redirect:
 *  - pro_kind='coach' → /atleti
 *  - pro_kind='nutri' o null → /clienti (legacy)
 *  - pro_kind='both' → /dashboard (la dashboard ha role switcher integrato)
 */
function LandingRedirect() {
  const { state } = useAuth();
  if (state.status !== 'authenticated' || state.user.role !== 'dietologist') {
    return <Navigate to="/dashboard" replace />;
  }
  const kind = state.user.pro_kind;
  if (kind === 'coach') return <Navigate to="/atleti" replace />;
  if (kind === 'both') return <Navigate to="/dashboard" replace />;
  return <Navigate to="/clienti" replace />;
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/debug" element={<Debug />} />
        <Route path="/" element={<ProtectedShell><LandingRedirect /></ProtectedShell>} />
        {/* Sprint 9.D — onboarding pro_kind selection post-register */}
        <Route path="/onboarding" element={<ProtectedShell><Onboarding /></ProtectedShell>} />
        <Route path="/dashboard" element={<ProtectedShell><Dashboard /></ProtectedShell>} />
        <Route path="/clienti" element={<ProtectedShell><Clienti /></ProtectedShell>} />
        <Route path="/atleti" element={<ProtectedShell><Atleti /></ProtectedShell>} />
        <Route path="/piani" element={<ProtectedShell><Piani /></ProtectedShell>} />
        {/* Sprint 9.B closure — lista template (PIÙ specifica di /piani/:id) */}
        <Route path="/piani/templates" element={<ProtectedShell><Templates /></ProtectedShell>} />
        {/* Sprint 9.B — editor wizard piano alimentare */}
        <Route path="/piani/new" element={<ProtectedShell><PianoEditor /></ProtectedShell>} />
        <Route path="/piani/:id/edit" element={<ProtectedShell><PianoEditor /></ProtectedShell>} />
        <Route path="/workout" element={<ProtectedShell><Workout /></ProtectedShell>} />
        {/* Sprint 7.2 — editor wizard programma */}
        <Route path="/programmi/new" element={<ProtectedShell><WorkoutProgramEditor /></ProtectedShell>} />
        <Route path="/programmi/:id/edit" element={<ProtectedShell><WorkoutProgramEditor /></ProtectedShell>} />
        {/* Coach routes. /programmi è la pagina reale (Programmi & PR = Workout).
            Le altre 3 NON sono ancora costruite → placeholder DISTINTI "in arrivo"
            (no più doppioni della stessa pagina Workout). Diventeranno pagine reali
            col modulo workout (incl. generazione AI). */}
        <Route path="/programmi" element={<ProtectedShell><Workout /></ProtectedShell>} />
        <Route
          path="/sessioni-live"
          element={
            <ProtectedShell>
              <ComingSoon
                title="Sessioni live"
                description="Avvia e segui in tempo reale le sessioni di allenamento dei tuoi atleti: serie, carichi e note aggiornate mentre si allenano."
                icon={<Clock size={26} strokeWidth={1.8} />}
              />
            </ProtectedShell>
          }
        />
        <Route
          path="/esercizi"
          element={
            <ProtectedShell>
              <ComingSoon
                title="Libreria esercizi"
                description="Catalogo degli esercizi con schede tecniche, gruppi muscolari, attrezzatura e varianti — il riferimento da cui costruire i programmi."
                icon={<Dumbbell size={26} strokeWidth={1.8} />}
              />
            </ProtectedShell>
          }
        />
        <Route
          path="/pr"
          element={
            <ProtectedShell>
              <ComingSoon
                title="PR & record"
                description="Storico completo dei record personali per atleta ed esercizio, con andamento nel tempo. Per ora trovi i PR recenti nella pagina Programmi."
                icon={<Trophy size={26} strokeWidth={1.8} />}
              />
            </ProtectedShell>
          }
        />
        {/* Nutri-only Sprint successive: catalogo, messaggi, impostazioni */}
        <Route path="/catalogo" element={<ProtectedShell><Catalogo /></ProtectedShell>} />
        <Route path="/messaggi" element={<ProtectedShell><Messaggi /></ProtectedShell>} />
        <Route path="/impostazioni" element={<ProtectedShell><Impostazioni /></ProtectedShell>} />
        <Route path="/analytics" element={<ProtectedShell><Analytics /></ProtectedShell>} />
        <Route path="/calendario" element={<ProtectedShell><Calendario /></ProtectedShell>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
